import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import HeroSearch from '../../components/feature/HeroSearch';
import FiltersBar from '../../components/feature/FiltersBar';
import PlaceCard from '../../components/feature/PlaceCard';
import BottomNav from '../../components/feature/BottomNav';
import PlaceDetailModal from '../../components/feature/PlaceDetailModal';
import { places } from '../../mocks/places';

// Calculate distance between two coordinates in km
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function Home() {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const [timeOfDay, setTimeOfDay] = useState<'day' | 'night' | 'all'>('all');
  const [openNow, setOpenNow] = useState(false);
  const [nearMe, setNearMe] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [budget, setBudget] = useState<number[]>([1, 2, 3]);
  const [selectedPlace, setSelectedPlace] = useState<typeof places[0] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const isEnglish = i18n.language === 'en';

  // SEO: Update document head
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    document.title = 'Discover Ulaanbaatar - Best Restaurants, Bars & Attractions | Mongolia Guide';

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(attrName, attrVal.replace(/"/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', 'Explore the best things to do in Ulaanbaatar, Mongolia. Find top-rated restaurants, nightlife, cultural attractions, spas, and hidden gems. Your ultimate guide to UB with ratings, reviews, and interactive maps.');
    setMeta('meta[name="keywords"]', 'content', 'Ulaanbaatar restaurants, Mongolia attractions, things to do in UB, Ulaanbaatar bars, Mongolia travel guide');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/`);

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setOg('og:title', 'Discover Ulaanbaatar - Best Restaurants, Bars & Attractions | Mongolia Guide');
    setOg('og:description', 'Explore the best things to do in Ulaanbaatar, Mongolia. Find top-rated restaurants, nightlife, cultural attractions, spas, and hidden gems.');
    setOg('og:url', `${siteUrl}/`);
    setOg('og:type', 'website');

    const setTwitter = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setTwitter('twitter:title', 'Discover Ulaanbaatar - Best Restaurants, Bars & Attractions');
    setTwitter('twitter:description', 'Explore the best things to do in Ulaanbaatar, Mongolia. Find top-rated restaurants, nightlife, cultural attractions, spas, and hidden gems.');

    // Schema.org JSON-LD
    const existingScript = document.querySelector('script[data-page-schema="home"]');
    if (existingScript) existingScript.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          'url': `${siteUrl}/`,
          'name': 'Discover Ulaanbaatar',
          'description': 'Your ultimate guide to the best restaurants, bars, attractions, and experiences in Ulaanbaatar, Mongolia.',
          'inLanguage': 'en-US',
          'potentialAction': {
            '@type': 'SearchAction',
            'target': {
              '@type': 'EntryPoint',
              'urlTemplate': `${siteUrl}/?q={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          }
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          'url': `${siteUrl}/`,
          'name': 'Discover Ulaanbaatar - Best Restaurants, Bars & Attractions | Mongolia Guide',
          'description': 'Explore the best things to do in Ulaanbaatar, Mongolia. Find top-rated restaurants, nightlife, cultural attractions, spas, and hidden gems.',
          'inLanguage': 'en-US',
          'isPartOf': { '@id': `${siteUrl}/#website` },
          'about': { '@id': `${siteUrl}/#destination` },
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${siteUrl}/` }
            ]
          }
        },
        {
          '@type': 'TouristDestination',
          '@id': `${siteUrl}/#destination`,
          'name': 'Ulaanbaatar',
          'alternateName': 'UB',
          'description': 'The capital city of Mongolia, offering diverse dining, nightlife, cultural attractions, and unique experiences.',
          'geo': {
            '@type': 'GeoCoordinates',
            'latitude': '47.9192',
            'longitude': '106.9177'
          },
          'containedInPlace': {
            '@type': 'Country',
            'name': 'Mongolia'
          },
          'touristType': ['Foodies', 'Culture Enthusiasts', 'Nightlife Seekers', 'Adventure Travelers']
        },
        {
          '@type': 'ItemList',
          '@id': `${siteUrl}/#placelist`,
          'name': 'Top Places in Ulaanbaatar',
          'description': 'Curated list of the best restaurants, bars, attractions, and experiences in Ulaanbaatar',
          'numberOfItems': places.length,
          'itemListElement': places.slice(0, 10).map((place, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'item': {
              '@type': place.category === 'eat' ? 'Restaurant' : place.category === 'party' ? 'BarOrPub' : 'TouristAttraction',
              'name': place.nameEn,
              'description': place.tip,
              'image': place.image,
              'aggregateRating': {
                '@type': 'AggregateRating',
                'ratingValue': place.rating,
                'bestRating': '5',
                'reviewCount': place.views
              },
              'geo': {
                '@type': 'GeoCoordinates',
                'latitude': place.lat.toString(),
                'longitude': place.lng.toString()
              }
            }
          }))
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'home');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-page-schema="home"]');
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  // Get user location when nearMe is enabled
  useEffect(() => {
    if (nearMe && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            setLocationError(null);
          },
          (error) => {
            setLocationError(isEnglish ? 'Unable to get your location' : 'Байршлыг тодорхойлох боломжгүй');
            setNearMe(false);
          }
        );
      } else {
        setLocationError(isEnglish ? 'Geolocation not supported' : 'Байршил тодорхойлох боломжгүй');
        setNearMe(false);
      }
    }
  }, [nearMe, userLocation, isEnglish]);

  const filteredPlaces = useMemo(() => {
    let result = places.filter(place => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = place.nameEn.toLowerCase().includes(query) || 
                           place.nameMn.toLowerCase().includes(query);
        const matchesTip = place.tip.toLowerCase().includes(query);
        if (!matchesName && !matchesTip) return false;
      }

      // Category filter
      if (selectedCategory !== 'all') {
        if (place.category !== selectedCategory) return false;
      }

      // Mood filter
      if (selectedMoods.length > 0) {
        const hasMatchingMood = selectedMoods.some(mood => place.mood.includes(mood));
        if (!hasMatchingMood) return false;
      }

      // Time filter
      if (timeOfDay !== 'all') {
        if (!place.timeOfDay.includes(timeOfDay)) return false;
      }

      // Open now filter
      if (openNow && !place.isOpen) return false;

      // Budget filter
      if (!budget.includes(place.budget)) return false;

      return true;
    });

    // Sort by distance if nearMe is enabled and we have user location
    if (nearMe && userLocation) {
      result = result
        .map(place => ({
          ...place,
          distance: calculateDistance(userLocation.lat, userLocation.lng, place.lat, place.lng)
        }))
        .sort((a, b) => a.distance - b.distance);
    }

    return result;
  }, [searchQuery, selectedCategory, selectedMoods, timeOfDay, openNow, budget, nearMe, userLocation]);

  const handleMoodToggle = (mood: string) => {
    setSelectedMoods(prev => 
      prev.includes(mood) 
        ? prev.filter(m => m !== mood)
        : [...prev, mood]
    );
  };

  const handleBudgetToggle = (level: number) => {
    setBudget(prev =>
      prev.includes(level)
        ? prev.filter(b => b !== level)
        : [...prev, level]
    );
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleNearMeToggle = () => {
    setNearMe(!nearMe);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      
      {/* Hero Search - Hidden on mobile, shown on desktop */}
      <div className="hidden md:block">
        <HeroSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      </div>
      
      <FiltersBar
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        selectedMoods={selectedMoods}
        onMoodToggle={handleMoodToggle}
        timeOfDay={timeOfDay}
        onTimeChange={setTimeOfDay}
        openNow={openNow}
        onOpenNowToggle={setOpenNow}
        nearMe={nearMe}
        onNearMeToggle={handleNearMeToggle}
        budget={budget}
        onBudgetToggle={handleBudgetToggle}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 pb-20 md:pb-6 pt-[calc(var(--filters-bar-height,200px)+4.5rem)] md:pt-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-3 md:mb-6 flex items-center justify-between">
            <p className="text-xs md:text-sm text-gray-600">
              {t('results.showing')} <strong className="text-gray-900">{filteredPlaces.length}</strong> {t('results.places')}
              {nearMe && userLocation && (
                <span className="ml-2 text-teal-600">
                  <i className="ri-navigation-fill mr-1"></i>
                  {isEnglish ? 'Sorted by distance' : 'Зайгаар эрэмбэлсэн'}
                </span>
              )}
            </p>
          </div>

          {/* Location Error Message */}
          {locationError && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {locationError}
            </div>
          )}

          {/* Grid Layout - 2 columns on mobile, 2 on tablet, 3 on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 lg:gap-6">
            {filteredPlaces.map(place => (
              <PlaceCard
                key={place.id}
                place={place}
                onClick={() => setSelectedPlace(place)}
                showDistance={nearMe && userLocation && 'distance' in place}
              />
            ))}
          </div>
          
          {filteredPlaces.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">
                {isEnglish 
                  ? 'No places found. Try adjusting your filters.'
                  : 'Газар олдсонгүй. Шүүлтүүрээ өөрчилж үзнэ үү.'}
              </p>
            </div>
          )}
        </div>
      </main>

      <BottomNav />

      {/* Place Detail Modal */}
      {selectedPlace && (
        <PlaceDetailModal
          place={selectedPlace}
          onClose={() => setSelectedPlace(null)}
        />
      )}
    </div>
  );
}